import React, { useCallback } from 'react';
import styled from 'styled-components';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { WorkspaceLiteData } from '../../api/ApiTypes';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { useLoaderWithRefresh } from '../../platform/Hook';
import { InfoView } from '../../ui/InfoView';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { FadeIn } from '../../ui/Transitions';
import { useResolve } from '../Container';
import { Crash } from '../Crash';
import { Participant } from '../canvas/participants/Participant';
import { Counter } from '../common/Users';
import { FlexFrame, Flexer, LinkButton } from '../dashboard/elements/LayoutElements';
import { workspaceDelete, workspaceInviteViaPopup, workspaceRemoveMember, workspaceSetPublic } from '../workspace/WorkspaceCommands';
import { Box } from './../../ui';

const ListCounter = styled.div`
    top: 1px;
    margin-right: .75em;
    font-size: .875rem;
`;

const { Section,
    LargeHeading, Heading, HeadingAction,
    DetailTable, DetailRow, DetailCell, DetailAction,
    ListItem, ListLabel, ListAction,
} = InfoView;

const InformationSection: LFC<{ workspace: WorkspaceLiteData }> = ({ workspace }) => {

    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);

    const [loading, info, err, reload] = useLoaderWithRefresh(
        async () => await api.exec(v2.admin.workspaces.info(workspace.id)),
        [workspace]
    );

    const onSetPublicClick = useCallback(async (value: boolean) => {
        if (await dispatch(workspaceSetPublic, [workspace.id, value])) {
            await reload();
        }
    }, [workspace]);

    const onDeleteClick = useCallback(async () => {
        if (await dispatch(workspaceDelete, [workspace.id])) {
            await reload();
        }
    }, [workspace]);

    if (err) return <Crash error={err} refreshOn403 />;

    return <Section>
        <LoadingGuard loading={loading}>
            <Heading>
                Information
                <HeadingAction onClick={onDeleteClick}>Delete Workspace</HeadingAction>
            </Heading>
            <DetailTable>
                <DetailRow>
                    <DetailCell shrink>Boards:</DetailCell>
                    <DetailCell>{info?.boardCount}</DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Folders:</DetailCell>
                    <DetailCell>{info?.folderCount}</DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Members:</DetailCell>
                    <DetailCell>{info?.userCount}</DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Created At:</DetailCell>
                    <DetailCell>{info && `${info.createdAt.toLocaleDateString()} @ ${info.createdAt.toLocaleTimeString()}`}</DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Public:</DetailCell>
                    <DetailCell>
                        {info && info.public ? 'Yes' : 'No'}
                        {info && info.public
                            ? <DetailAction onClick={loading ? null : () => onSetPublicClick(false)}>Make Private</DetailAction>
                            : <DetailAction onClick={loading ? null : () => onSetPublicClick(true)}>Make Public</DetailAction>
                        }
                    </DetailCell>
                </DetailRow>
            </DetailTable>
        </LoadingGuard>
    </Section>;
};

const MembersSection: LFC<{ workspace: WorkspaceLiteData }> = ({ workspace }) => {

    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);

    const [loading, members, err, reload] = useLoaderWithRefresh(
        async () => await api.exec(v2.workspaces.members.list(workspace.id)),
        [workspace]
    );

    const onInviteClick = useCallback(async () => {
        if (await dispatch(workspaceInviteViaPopup, [workspace.id])) {
            await reload();
        }
    }, [workspace]);

    const onRemoveClick = useCallback(async (userId: string) => {
        if (await dispatch(workspaceRemoveMember, [userId, workspace.id])) {
            await reload();
        }
    }, [workspace]);

    if (err) return <Crash error={err} refreshOn403 />;

    return <Section>
        <LoadingGuard loading={loading}>
            <Heading>
                Members
                <HeadingAction onClick={onInviteClick}>
                    <Icon size="1" src="deqo://icons/hero/plus" />&nbsp;Invite People
                </HeadingAction>
            </Heading>
            {members && members.map(x =>
                <ListItem key={x.id}>
                    <ListCounter>
                        <Counter size={2.0} color={x.color} photo={x.photo}>{Participant.toInitials(x.name)}</Counter>
                    </ListCounter>
                    <ListLabel>{x.name}</ListLabel>
                    <ListAction onClick={() => onRemoveClick(x.id)}>Remove</ListAction>
                </ListItem>
            )}
            {!members && <Box height="10" />}
        </LoadingGuard>
    </Section>;
};

export const ManageWorkspacesDetailView: LFC<{ workspace: WorkspaceLiteData }> = ({ workspace }) =>
    <FadeIn>
        <FlexFrame>
            <Section>
                <LargeHeading>
                    {workspace.label}
                    <Flexer margin="0 .5">
                        <LinkButton to={`~/${workspace.id}`} size="xs" label="Open Workspace" />
                    </Flexer>
                </LargeHeading>
            </Section>
            <InformationSection workspace={workspace} />
            <MembersSection workspace={workspace} />
        </FlexFrame>
    </FadeIn>;
